import type { Invitation } from "../../shared/invitations";
import type { Session } from "./api";
import type { AccountDetail, AccountSummary, CacheEntry, Investor } from "./types";

export const demoSession: Session = {
  user: {
    id: "00000000-0000-4000-8000-000000000001",
    name: "Alex Devon",
    email: "alex@evidite.com"
  },
  permissions: [
    "accounts.read",
    "accounts.manage",
    "wallets.adjust",
    "telemetry.read",
    "investors.read",
    "investors.write",
    "cache.read",
    "cache.invalidate",
    "stacks.read",
    "stacks.search",
    "invitations.manage",
    "staff.manage"
  ],
  csrfToken: "illustrative-local-demo"
};

export const demoAccounts: AccountSummary[] = [
  {
    id: "10000000-0000-4000-8000-000000000001",
    name: "Maya Ellison",
    email: "maya@example.org",
    email_verified: true,
    status: "active",
    legacy_source: "geniecore",
    created_at: "2026-08-02T14:11:00Z",
    last_seen_at: "2026-08-13T17:34:00Z",
    global_balance: "84",
    app_balance: "0"
  },
  {
    id: "10000000-0000-4000-8000-000000000002",
    name: "Daniel Cho",
    email: "daniel@example.org",
    email_verified: true,
    status: "active",
    legacy_source: "blindnotegenie",
    created_at: "2026-07-19T09:24:00Z",
    last_seen_at: "2026-08-12T19:08:00Z",
    global_balance: "25",
    app_balance: "6"
  },
  {
    id: "10000000-0000-4000-8000-000000000003",
    name: "Nadia Ruiz",
    email: "nadia@example.org",
    email_verified: false,
    status: "pending_activation",
    legacy_source: null,
    created_at: "2026-08-11T20:02:00Z",
    last_seen_at: null,
    global_balance: "0",
    app_balance: "0"
  },
  {
    id: "10000000-0000-4000-8000-000000000004",
    name: "Owen Hart",
    email: "owen@example.org",
    email_verified: true,
    status: "blocked",
    legacy_source: "geniecore",
    created_at: "2026-06-03T10:45:00Z",
    last_seen_at: "2026-08-08T21:10:00Z",
    global_balance: "-4",
    app_balance: "0"
  }
];

export function demoAccountDetail(account: AccountSummary): AccountDetail {
  return {
    account: {
      ...account,
      two_factor_enabled: account.email_verified,
      staff_account: account.email === "daniel@example.org",
      locale: "en-US",
      timezone: "America/New_York",
      updated_at: account.created_at
    },
    wallets: [
      {
        id: "20000000-0000-4000-8000-000000000001",
        scope: "global",
        application_id: null,
        application_name: null,
        balance: account.global_balance,
        status: Number(account.global_balance) < 0 ? "blocked" : "active"
      },
      ...(account.app_balance !== "0"
        ? [
            {
              id: "20000000-0000-4000-8000-000000000002",
              scope: "application",
              application_id: "blindnotegenie",
              application_name: "BlindnoteGenie",
              balance: account.app_balance,
              status: "active"
            }
          ]
        : [])
    ],
    events: [
      {
        id: "e1",
        wallet_id: "20000000-0000-4000-8000-000000000001",
        event_type: "purchase",
        amount: "50",
        balance_after: account.global_balance,
        metadata: {},
        created_at: "2026-08-12T16:22:00Z"
      },
      {
        id: "e2",
        wallet_id: "20000000-0000-4000-8000-000000000001",
        event_type: "usage",
        amount: "-1",
        balance_after: String(Number(account.global_balance) - 50),
        metadata: {},
        created_at: "2026-08-11T13:08:00Z"
      }
    ],
    migrations: account.legacy_source
      ? [
          {
            source_system: account.legacy_source,
            source_type: "user",
            source_id: "legacy-demo",
            migrated_at: "2026-08-12T14:00:00Z"
          }
        ]
      : [],
    entitlements:
      account.app_balance !== "0"
        ? [
            {
              application_id: "blindnotegenie",
              application_name: "BlindnoteGenie",
              entitlement_key: "unlimited",
              status: "active",
              starts_at: "2026-08-12T14:00:00Z",
              ends_at: null
            }
          ]
        : []
  };
}

export const demoInvestors: Investor[] = [
  {
    id: "30000000-0000-4000-8000-000000000001",
    legacy_id: "12",
    name: "North Bridge Ventures",
    background: "Applied AI and professional software.",
    category: "Seed fund",
    stage: "diligence",
    target_ask: "750000",
    next_action: "Send product-data follow-up",
    next_action_date: "2026-08-15",
    notes: "Second meeting complete.",
    updated_at: "2026-08-12T15:02:00Z"
  },
  {
    id: "30000000-0000-4000-8000-000000000002",
    legacy_id: "24",
    name: "Keystone Capital",
    background: "Vertical SaaS and research tooling.",
    category: "Pre-seed fund",
    stage: "meeting_scheduled",
    target_ask: "500000",
    next_action: "Prepare workflow demo",
    next_action_date: "2026-08-18",
    notes: null,
    updated_at: "2026-08-11T12:30:00Z"
  },
  {
    id: "30000000-0000-4000-8000-000000000003",
    legacy_id: "31",
    name: "Aster Labs Fund",
    background: "Technical founder network.",
    category: "Angel syndicate",
    stage: "research",
    target_ask: null,
    next_action: "Find warm introduction",
    next_action_date: null,
    notes: null,
    updated_at: "2026-08-09T17:10:00Z"
  }
];

export const demoCacheEntries: CacheEntry[] = [
  {
    id: "4f0f2e34-6254-4acf-bba7-91ee73498100",
    canonical_id: "10.1000/example.1",
    canonical_type: "doi",
    source_engine: "crossref",
    is_paid_source: false,
    hit_count: 18,
    fetched_at: "2026-08-12T14:20:00Z",
    expires_at: "2026-09-11T14:20:00Z",
    title_preview: "An illustrative cached research article",
    authors_preview: "Rivera et al.",
    url: "https://doi.org/10.1000/example.1"
  },
  {
    id: "6b8e9878-e31e-4202-aec5-c673a67e3700",
    canonical_id: "PMID:123456",
    canonical_type: "pmid",
    source_engine: "pubmed",
    is_paid_source: false,
    hit_count: 6,
    fetched_at: "2026-08-10T09:30:00Z",
    expires_at: "2026-09-09T09:30:00Z",
    title_preview: "A second illustrative cache record",
    authors_preview: "Chen et al.",
    url: null
  }
];

const daysAgo = (days: number) => new Date(Date.now() - days * 86_400_000).toISOString();

export const demoInvitations: Invitation[] = [
  {
    id: "7d0f0f7e-2a4b-4f0e-9d0a-000000000001",
    email: "jordan.reyes@lawfirm.example",
    status: "pending",
    invitedBy: "alex@evidite.com",
    createdAt: daysAgo(1),
    expiresAt: daysAgo(-6),
    acceptedAt: null,
    revokedAt: null
  },
  {
    id: "7d0f0f7e-2a4b-4f0e-9d0a-000000000002",
    email: "priya.nair@university.example",
    status: "accepted",
    invitedBy: "alex@evidite.com",
    createdAt: daysAgo(9),
    expiresAt: daysAgo(-5),
    acceptedAt: daysAgo(8),
    revokedAt: null
  },
  {
    id: "7d0f0f7e-2a4b-4f0e-9d0a-000000000003",
    email: "sam.okafor@publisher.example",
    status: "expired",
    invitedBy: "alex@evidite.com",
    createdAt: daysAgo(40),
    expiresAt: daysAgo(33),
    acceptedAt: null,
    revokedAt: null
  },
  {
    id: "7d0f0f7e-2a4b-4f0e-9d0a-000000000004",
    email: "person@example.com",
    status: "revoked",
    invitedBy: "alex@evidite.com",
    createdAt: daysAgo(2),
    expiresAt: daysAgo(-5),
    acceptedAt: null,
    revokedAt: daysAgo(0)
  }
];
